import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Settings, Check, AlertCircle, Plus, Trash2, RefreshCw, ShieldCheck, Clock, Star, Server, KeyRound, ChevronDown, ChevronUp, Info, Bell, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';
const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('authToken')}` });

// Monochrome brand-style icons (single tone, theme-aware via currentColor).
const GmailIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" />
  </svg>
);

const OutlookIcon = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86 0-.46.1-.87.1-.41.33-.73.22-.31.58-.5.37-.2.87-.2t.85.2q.35.19.57.5.22.32.33.73.1.41.1.87zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.31.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zm-6-8.25v3h3v-3zm0 4.5v3h3v-3zm0 4.5v1.83l3.05-1.83zm-5.25-9v3h3.75v-3zm0 4.5v3h3.75v-3zm0 4.5v2.03l2.41 1.4 1.34-.8v-2.63zM9 3.75V6h2l.13.01.12.04v-2.3zM5.98 15.98q.9 0 1.6-.3.7-.32 1.19-.86.48-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24t-1.15-.83q-.68-.3-1.55-.3-.92 0-1.64.3-.71.3-1.2.85-.5.54-.75 1.3-.25.74-.25 1.63 0 .85.26 1.56.26.72.74 1.23.48.52 1.17.81.69.3 1.56.3zM7.5 21h12.39L12.5 16.74V18q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z" />
  </svg>
);

// Top email providers with their SMTP + IMAP defaults.
// Single-tone styling: each card uses the same neutral scheme; selection adds a blue ring.
const EMAIL_PROVIDERS = [
  {
    id: 'gmail',
    name: 'Gmail',
    Icon: GmailIcon,
    domains: ['gmail.com', 'googlemail.com'],
    smtp: { host: 'smtp.gmail.com', port: 465, secure: true },
    imap: { host: 'imap.gmail.com', port: 993, secure: true },
    helpText: 'Gmail requires an App Password (2FA must be on). Use that — not your normal password.',
    helpUrl: 'https://support.google.com/accounts/answer/185833'
  },
  {
    id: 'outlook',
    name: 'Outlook',
    Icon: OutlookIcon,
    domains: ['outlook.com', 'hotmail.com', 'live.com', 'msn.com'],
    smtp: { host: 'smtp-mail.outlook.com', port: 587, secure: false },
    imap: { host: 'outlook.office365.com', port: 993, secure: true },
    helpText: 'Outlook/Hotmail accounts: use your regular password, or an App Password if 2FA is enabled.',
    helpUrl: 'https://support.microsoft.com/account-billing/how-to-get-and-use-app-passwords-5896ed9b-4263-e681-128a-a6f2979a7944'
  },
  {
    id: 'yahoo',
    name: 'Yahoo',
    letter: 'Y',
    domains: ['yahoo.com', 'ymail.com', 'rocketmail.com'],
    smtp: { host: 'smtp.mail.yahoo.com', port: 465, secure: true },
    imap: { host: 'imap.mail.yahoo.com', port: 993, secure: true },
    helpText: 'Yahoo requires an App Password generated in Account Security settings.',
    helpUrl: 'https://help.yahoo.com/kb/SLN15241.html'
  },
  {
    id: 'icloud',
    name: 'iCloud',
    letter: 'A',
    domains: ['icloud.com', 'me.com', 'mac.com'],
    smtp: { host: 'smtp.mail.me.com', port: 587, secure: false },
    imap: { host: 'imap.mail.me.com', port: 993, secure: true },
    helpText: 'iCloud requires an App-Specific Password from appleid.apple.com (Sign-In & Security).',
    helpUrl: 'https://support.apple.com/en-us/HT204397'
  },
  {
    id: 'one',
    name: 'One.com',
    letter: '1',
    domains: ['one.com'],
    smtp: { host: 'send.one.com', port: 465, secure: true },
    imap: { host: 'imap.one.com', port: 993, secure: true },
    helpText: 'Use the mailbox password set inside the One.com control panel.',
    helpUrl: 'https://help.one.com/hc/en-us/articles/115005594405'
  },
  {
    id: 'zoho',
    name: 'Zoho',
    letter: 'Z',
    domains: ['zoho.com', 'zohomail.com'],
    smtp: { host: 'smtp.zoho.com', port: 465, secure: true },
    imap: { host: 'imap.zoho.com', port: 993, secure: true },
    helpText: 'Zoho usually needs an App Password if 2FA is enabled. Generate it in your Zoho account security settings.',
    helpUrl: 'https://www.zoho.com/mail/help/imap-access.html'
  },
  {
    id: 'yandex',
    name: 'Yandex',
    letter: 'Я',
    domains: ['yandex.com', 'yandex.ru', 'ya.ru'],
    smtp: { host: 'smtp.yandex.com', port: 465, secure: true },
    imap: { host: 'imap.yandex.com', port: 993, secure: true },
    helpText: 'Enable IMAP access in Yandex Mail settings and create an App Password if 2FA is enabled.',
    helpUrl: 'https://yandex.com/support/mail/mail-clients/others.html'
  },
  {
    id: 'aol',
    name: 'AOL',
    letter: 'A',
    domains: ['aol.com'],
    smtp: { host: 'smtp.aol.com', port: 465, secure: true },
    imap: { host: 'imap.aol.com', port: 993, secure: true },
    helpText: 'AOL requires an App Password generated in Account Security settings.',
    helpUrl: 'https://help.aol.com/articles/Create-and-manage-app-password'
  },
  {
    id: 'gmx',
    name: 'GMX',
    letter: 'G',
    domains: ['gmx.com', 'gmx.net', 'gmx.de'],
    smtp: { host: 'mail.gmx.com', port: 465, secure: true },
    imap: { host: 'imap.gmx.com', port: 993, secure: true },
    helpText: 'Enable POP3/IMAP access inside GMX webmail settings before connecting.',
    helpUrl: 'https://www.gmx.com/email/pop-imap/'
  },
  {
    id: 'fastmail',
    name: 'Fastmail',
    letter: 'F',
    domains: ['fastmail.com', 'fastmail.fm'],
    smtp: { host: 'smtp.fastmail.com', port: 465, secure: true },
    imap: { host: 'imap.fastmail.com', port: 993, secure: true },
    helpText: 'Fastmail requires an App Password — create one under Settings → Password & Security.',
    helpUrl: 'https://www.fastmail.help/hc/en-us/articles/360058752394'
  },
  {
    id: 'other',
    name: 'Other',
    isOther: true,
    domains: [],
    helpText: 'Enter your server details manually. Check your provider\'s documentation for SMTP and IMAP host, port, and SSL/TLS requirements.'
  }
];

const EmailSettings = () => {
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isAddOpen, setIsAddOpen] = useState(false);

  // Add-form state
  const [form, setForm] = useState({
    email: '',
    display_name: '',
    smtp: { host: '', port: 465, secure: true, username: '', password: '' },
    imap: { host: '', port: 993, secure: true, username: '', password: '' },
    set_primary: false
  });
  const [providerHelp, setProviderHelp] = useState(null);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);

  // Host/port/SSL stay hidden for a recognised provider — the preset already has
  // them right, so showing six server fields only invites the user to break
  // them. Picking "Other" opens them, and there is a manual toggle for the rest.
  const [showServers, setShowServers] = useState(false);

  // For every provider listed here the login IS the email address. Asking for it
  // twice — once as "Email address", again as SMTP/IMAP "Username" — read as two
  // different values and was the confusing part. One box fills all three; the
  // per-transport override still exists inside the server settings block for the
  // rare host whose username differs.
  const applyEmail = (email) => {
    setForm(prev => ({
      ...prev,
      email,
      smtp: { ...prev.smtp, username: email },
      imap: { ...prev.imap, username: email }
    }));
    setTestResult(null);
  };

  // Likewise one password box feeds both SMTP and IMAP.
  const applyPassword = (password) => {
    setForm(prev => ({
      ...prev,
      smtp: { ...prev.smtp, password },
      imap: { ...prev.imap, password }
    }));
    setTestResult(null);
  };

  // One-click provider preset — fills SMTP + IMAP host/port/secure so the
  // user only has to type their email and password.
  // "Other" clears server fields so the user can fill them manually.
  const selectProvider = (provider) => {
    setSelectedProviderId(provider.id);
    // "Other" is the only case where the user has to see server fields — there is
    // no preset to fill them with.
    setShowServers(!!provider.isOther);
    if (provider.isOther) {
      setForm(prev => ({
        ...prev,
        smtp: { ...prev.smtp, host: '', port: 465, secure: true },
        imap: { ...prev.imap, host: '', port: 993, secure: true }
      }));
      setProviderHelp(provider.helpText ? { text: provider.helpText, url: null } : null);
    } else {
      setForm(prev => ({
        ...prev,
        smtp: {
          ...prev.smtp,
          host: provider.smtp.host,
          port: provider.smtp.port,
          secure: provider.smtp.secure
        },
        imap: {
          ...prev.imap,
          host: provider.imap.host,
          port: provider.imap.port,
          secure: provider.imap.secure
        }
      }));
      setProviderHelp(provider.helpText && provider.helpUrl
        ? { text: provider.helpText, url: provider.helpUrl }
        : null);
    }
    setTestResult(null);
  };

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/list`, { credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      if (data.success) setAccounts(data.data);
    } catch (err) { console.error('Fetch accounts failed:', err); }
    setIsLoading(false);
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 6000);
  };

  // When user finishes typing email, auto-pull provider defaults
  const handleEmailBlur = async () => {
    if (!form.email || !form.email.includes('@')) return;
    const domain = form.email.split('@')[1]?.toLowerCase();
    const match = EMAIL_PROVIDERS.find(p => p.domains.includes(domain));
    if (match) setSelectedProviderId(match.id);
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/provider-defaults?email=${encodeURIComponent(form.email)}`, {
        credentials: 'include', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success && data.data) {
        const d = data.data;
        // username tracks the email rather than `|| email`, so correcting a typo
        // in the address cannot leave the old one behind as the login.
        setForm(prev => ({
          ...prev,
          smtp: { ...prev.smtp, host: d.smtp.host, port: d.smtp.port, secure: d.smtp.secure, username: form.email },
          imap: { ...prev.imap, host: d.imap.host, port: d.imap.port, secure: d.imap.secure, username: form.email }
        }));
        setProviderHelp(d.helpText && d.helpUrl ? { text: d.helpText, url: d.helpUrl } : null);
      } else {
        // Unknown provider — the address is still the login, but the hosts have to
        // be typed, so open the server block instead of hiding empty required fields.
        setForm(prev => ({
          ...prev,
          smtp: { ...prev.smtp, username: form.email },
          imap: { ...prev.imap, username: form.email }
        }));
        setProviderHelp(null);
        setShowServers(true);
      }
    } catch (err) {
      // Detection is the only thing that fills the hidden host fields, so if it
      // fails the user must be given them — otherwise Test connection stays
      // disabled with nothing on screen explaining why.
      console.warn('Provider defaults fetch failed:', err);
      setShowServers(true);
    }
  };

  // No longer mirrors into IMAP: applyEmail/applyPassword already keep both
  // transports in step, and mirroring here would overwrite an IMAP username that
  // the user had deliberately set to something different.
  const updateSmtp = (patch) => {
    setForm(prev => ({ ...prev, smtp: { ...prev.smtp, ...patch } }));
    setTestResult(null); // invalidate test on field change
  };
  const updateImap = (patch) => {
    setForm(prev => ({ ...prev, imap: { ...prev.imap, ...patch } }));
    setTestResult(null);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ smtp: form.smtp, imap: form.imap })
      });
      const data = await res.json();
      if (data.success) setTestResult(data.data);
      else showMessage('error', data.message || 'Test failed');
    } catch (err) { showMessage('error', 'Test request failed: ' + err.message); }
    setTesting(false);
  };

  const handleSave = async () => {
    if (!form.email.includes('@')) return showMessage('error', 'Valid email required');
    if (!form.smtp.password) return showMessage('error', 'Password required');
    // The server fields are hidden by default, so say plainly that they are the
    // thing missing — otherwise the backend's "complete smtp credentials
    // required" arrives about fields the user cannot see.
    if (!form.smtp.host || !form.imap.host) {
      setShowServers(true);
      return showMessage('error', "We couldn't detect your mail server — please fill in the server settings below.");
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/add-smtp`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        showMessage('success', data.message || 'Account saved');
        setIsAddOpen(false);
        setForm({
          email: '', display_name: '',
          smtp: { host: '', port: 465, secure: true, username: '', password: '' },
          imap: { host: '', port: 993, secure: true, username: '', password: '' },
          set_primary: false
        });
        setSelectedProviderId(null);
        setProviderHelp(null);
        setTestResult(null);
        setShowServers(false);
        fetchAccounts();
      } else {
        showMessage('error', data.message || 'Save failed');
        if (data.data) setTestResult(data.data); // server tested before save and failed
      }
    } catch (err) { showMessage('error', 'Save failed: ' + err.message); }
    setSaving(false);
  };

  const handleRemove = async (item) => {
    if (!window.confirm(`Remove ${item.email}?`)) return;
    const url = item.source === 'smtp'
      ? `${API_URL}/api/senderEmail/remove-smtp/${item.id}`
      : `${API_URL}/api/senderEmail/remove/${item.id}`;
    try {
      const res = await fetch(url, { method: 'DELETE', credentials: 'include', headers: authHeaders() });
      const data = await res.json();
      if (data.success) { showMessage('success', 'Removed'); fetchAccounts(); }
      else showMessage('error', data.message || 'Remove failed');
    } catch (err) { showMessage('error', err.message); }
  };

  const handleSetPrimary = async (item) => {
    if (item.source !== 'smtp') return;
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/set-primary-smtp/${item.id}`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) { showMessage('success', 'Primary updated'); fetchAccounts(); }
      else showMessage('error', data.message || 'Update failed');
    } catch (err) { showMessage('error', err.message); }
  };

  const [checkingReplies, setCheckingReplies] = useState(null); // account id currently being scanned
  const handleCheckReplies = async (item) => {
    if (item.source !== 'smtp') return;
    setCheckingReplies(item.id);
    try {
      const res = await fetch(`${API_URL}/api/email-fetch/reply-tracker/run-now/${item.id}`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success) {
        const n = data.data?.totalNewReplies || 0;
        showMessage(n > 0 ? 'success' : 'success', data.message || (n > 0 ? `Found ${n} new` : 'No new replies'));
      } else {
        showMessage('error', data.message || 'Reply check failed');
      }
    } catch (err) { showMessage('error', err.message); }
    setCheckingReplies(null);
  };

  const handleRetest = async (item) => {
    if (item.source !== 'smtp') return;
    try {
      const res = await fetch(`${API_URL}/api/senderEmail/test-existing/${item.id}`, {
        method: 'POST', credentials: 'include', headers: authHeaders()
      });
      const data = await res.json();
      if (data.success && data.data.ok) showMessage('success', 'Connection OK');
      else if (data.success) showMessage('error', `SMTP: ${data.data.smtp.error || 'ok'} | IMAP: ${data.data.imap.error || 'ok'}`);
      else showMessage('error', data.message || 'Re-test failed');
      fetchAccounts();
    } catch (err) { showMessage('error', err.message); }
  };

  const smtpCount = accounts.filter(a => a.source === 'smtp').length;
  const verifiedCount = accounts.filter(a => a.is_verified).length;

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950" style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100 flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                Email Settings
              </h1>
              <p className="text-gray-600 dark:text-slate-400 leading-relaxed max-w-xl">
                Connect your own email account using SMTP + IMAP. Sending uses your real address (better deliverability) and you'll be able to read your Inbox + Sent folder inside the app.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="px-4 py-2 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/30 dark:to-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 shadow-sm">
                <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                  {verifiedCount} / {accounts.length} verified
                </span>
              </div>
              <button onClick={fetchAccounts} className="p-2 text-gray-500 dark:text-slate-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg" title="Refresh">
                <RefreshCw className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Banner: a legacy account can send but has no inbox access */}
      {smtpCount === 0 && accounts.length > 0 && (
        <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Connect SMTP + IMAP for inbox access</p>
            <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              Your sender address was set up on the older method, which can send but cannot read mail. Add it below with SMTP+IMAP to see your inbox and sent folder inside the app.
            </p>
          </div>
        </div>
      )}

      {/* Account list */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
          <div className="flex items-center gap-2 px-6 pt-6 pb-4 border-b border-gray-100 dark:border-slate-800">
            <Mail className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Your Email Accounts</h2>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full" />
              <span className="ml-3 text-gray-500 dark:text-slate-400 text-sm">Loading...</span>
            </div>
          ) : accounts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6">
              <div className="p-3 bg-gray-100 dark:bg-slate-800 rounded-full mb-3">
                <Mail className="w-8 h-8 text-gray-400" />
              </div>
              <p className="text-gray-600 dark:text-slate-400 font-medium">No email accounts yet</p>
              <p className="text-gray-400 dark:text-slate-500 text-sm mt-1">Click "Add account" below to connect your first email</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-slate-800">
              {accounts.map(item => (
                <div key={`${item.source}-${item.id}`} className="flex items-center justify-between px-6 py-4 gap-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${item.is_verified ? 'bg-green-50 dark:bg-green-900/20' : 'bg-amber-50 dark:bg-amber-900/20'}`}>
                      {item.is_verified
                        ? <ShieldCheck className="w-4 h-4 text-green-600 dark:text-green-400" />
                        : <Clock className="w-4 h-4 text-amber-500 dark:text-amber-400" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-800 dark:text-slate-100 truncate">{item.email}</span>
                        {item.is_primary && (
                          <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full font-medium">Primary</span>
                        )}
                        {/* Only the SMTP+IMAP capability is worth naming. A legacy
                            row used to be badged "Legacy SES", which named an
                            internal storage column as if it were the user's mail
                            provider — and for a signup pre-fill, nothing had ever
                            touched SES at all. */}
                        {item.source === 'smtp' && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                            SMTP+IMAP
                          </span>
                        )}
                        {item.source !== 'smtp' && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-400">
                            Send only
                          </span>
                        )}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.is_verified ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'}`}>
                          {item.is_verified ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                      {item.last_test_error && (
                        <p className="text-xs text-red-500 mt-0.5 truncate" title={item.last_test_error}>
                          Last test: {item.last_test_error}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {item.source === 'smtp' && !item.is_primary && (
                      <button onClick={() => handleSetPrimary(item)} title="Set as primary"
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg">
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    {item.source === 'smtp' && (
                      <button onClick={() => handleCheckReplies(item)} title="Check for replies now"
                        disabled={checkingReplies === item.id}
                        className="p-2 text-gray-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg disabled:opacity-50">
                        <Bell className={`w-4 h-4 ${checkingReplies === item.id ? 'animate-pulse' : ''}`} />
                      </button>
                    )}
                    {item.source === 'smtp' && (
                      <button onClick={() => handleRetest(item)} title="Re-test connection"
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg">
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    )}
                    {/* Every row is removable. This used to be gated on
                        `smtp || !is_primary`, which left a legacy row that was also
                        primary with no delete button at all — even though
                        DELETE /remove/primary has always supported it. */}
                    <button onClick={() => handleRemove(item)} title="Remove"
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {message.text && (
        <div className={`mb-4 p-4 rounded-xl border flex items-center gap-2 ${
          message.type === 'success'
            ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
            : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
        }`}>
          {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span className="font-medium text-sm">{message.text}</span>
        </div>
      )}

      {/* Add account (collapsible) */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm">
          <button
            onClick={() => setIsAddOpen(!isAddOpen)}
            className="w-full flex items-center justify-between px-6 py-4 text-left"
          >
            <div className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Add email account</h2>
            </div>
            {isAddOpen ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />}
          </button>

          {isAddOpen && (
            <div className="px-6 pb-6 border-t border-gray-100 dark:border-slate-800 pt-4 space-y-5">
              {/* Quick provider picker */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-slate-200">
                    Quick setup — pick your email provider
                  </h3>
                  <span className="text-xs text-gray-500 dark:text-slate-500">
                    (fills server settings automatically — you only enter email & password)
                  </span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {EMAIL_PROVIDERS.map(p => {
                    const isSelected = selectedProviderId === p.id;
                    const ProviderIcon = p.Icon;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => selectProvider(p)}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border transition-all text-left bg-white hover:bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700 dark:border-slate-700 dark:text-slate-200 ${
                          isSelected
                            ? 'ring-2 ring-blue-500 dark:ring-blue-400 border-blue-300 dark:border-blue-700 shadow-sm'
                            : 'hover:shadow-sm'
                        }`}
                        title={p.isOther ? 'Enter server details manually' : `Use ${p.name} settings`}
                      >
                        <span className="flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold flex-shrink-0">
                          {ProviderIcon
                            ? <ProviderIcon className="w-4 h-4" />
                            : p.isOther
                              ? <Plus className="w-4 h-4" />
                              : p.letter}
                        </span>
                        <span className="text-sm font-medium truncate">{p.name}</span>
                        {isSelected && <Check className="w-4 h-4 ml-auto flex-shrink-0 text-blue-600 dark:text-blue-400" />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-500 mt-2">
                  Don't see your provider? Just type your email below — we'll detect it, or you can fill the server details manually.
                </p>
              </div>

              <div className="border-t border-gray-100 dark:border-slate-800" />

              {/* Email + password — for a listed provider these are the only two
                  things needed, so they sit together and nothing else is shown. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Email address *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => applyEmail(e.target.value)}
                    onBlur={handleEmailBlur}
                    placeholder="you@example.com"
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                    This is also the username used to log in to your mail server.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Password *</label>
                  <input
                    type="password"
                    value={form.smtp.password}
                    onChange={e => applyPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-slate-100"
                  />
                  <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                    Most providers need an app password, not your normal one.
                  </p>
                </div>
              </div>

              <div className="sm:max-w-sm">
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">Display name (optional)</label>
                <input
                  type="text"
                  value={form.display_name}
                  onChange={e => setForm({ ...form, display_name: e.target.value })}
                  placeholder="Your Name"
                  className="w-full px-3 py-2.5 border border-gray-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-slate-100"
                />
              </div>

              {providerHelp && (
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    {providerHelp.text}
                    {providerHelp.url && (
                      <>
                        {' '}
                        <a href={providerHelp.url} target="_blank" rel="noopener noreferrer" className="underline font-medium">Learn how →</a>
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Server settings — collapsed for a recognised provider, whose preset
                  already has host/port/SSL right. Opens by itself for "Other" or an
                  unrecognised domain, where the user has to supply them. The
                  username fields live here too: they default to the email address,
                  and only a rare host needs them to differ. */}
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg">
                <button
                  type="button"
                  onClick={() => setShowServers(v => !v)}
                  className="w-full flex items-center gap-2 px-4 py-3 text-left"
                >
                  <Server className="w-4 h-4 text-gray-500 dark:text-slate-400 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-200">Server settings</span>
                  <span className="text-xs text-gray-500 dark:text-slate-500 truncate">
                    {selectedProviderId && selectedProviderId !== 'other'
                      ? 'filled in automatically — you should not need to touch these'
                      : 'SMTP and IMAP host, port and SSL'}
                  </span>
                  {showServers
                    ? <ChevronUp className="w-4 h-4 text-gray-500 ml-auto flex-shrink-0" />
                    : <ChevronDown className="w-4 h-4 text-gray-500 ml-auto flex-shrink-0" />}
                </button>

                {showServers && (
                  <div className="px-4 pb-4 pt-4 space-y-4 border-t border-gray-100 dark:border-slate-800">
                    {/* SMTP */}
                    <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
                        <Server className="w-4 h-4" /> SMTP (sending)
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Host</label>
                          <input type="text" value={form.smtp.host} onChange={e => updateSmtp({ host: e.target.value })} placeholder="smtp.gmail.com" className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Port</label>
                          <input type="number" value={form.smtp.port} onChange={e => updateSmtp({ port: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Username — leave as your email unless your host says otherwise</label>
                        <input type="text" value={form.smtp.username} onChange={e => updateSmtp({ username: e.target.value })} placeholder={form.email || 'you@example.com'} className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                        <input type="checkbox" checked={form.smtp.secure} onChange={e => updateSmtp({ secure: e.target.checked })} />
                        Use SSL/TLS (uncheck for STARTTLS on port 587)
                      </label>
                    </div>

                    {/* IMAP */}
                    <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-slate-200">
                        <KeyRound className="w-4 h-4" /> IMAP (reading inbox / sent)
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div className="sm:col-span-2">
                          <label className="block text-xs text-gray-500 mb-1">Host</label>
                          <input type="text" value={form.imap.host} onChange={e => updateImap({ host: e.target.value })} placeholder="imap.gmail.com" className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">Port</label>
                          <input type="number" value={form.imap.port} onChange={e => updateImap({ port: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Username — leave as your email unless your host says otherwise</label>
                        <input type="text" value={form.imap.username} onChange={e => updateImap({ username: e.target.value })} placeholder={form.email || 'you@example.com'} className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 rounded-md text-sm dark:bg-slate-800 dark:text-slate-100" />
                      </div>
                      <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400">
                        <input type="checkbox" checked={form.imap.secure} onChange={e => updateImap({ secure: e.target.checked })} />
                        Use SSL/TLS
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Test result */}
              {testResult && (
                <div className={`rounded-lg p-3 border ${testResult.ok ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800'}`}>
                  <div className="flex items-start gap-2 text-sm">
                    {testResult.ok ? <Check className="w-4 h-4 text-green-600 mt-0.5" /> : <AlertCircle className="w-4 h-4 text-red-600 mt-0.5" />}
                    <div className="flex-1">
                      <div className={testResult.smtp.ok ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}>
                        SMTP: {testResult.smtp.ok ? 'OK' : `Failed — ${testResult.smtp.error}`}
                      </div>
                      <div className={testResult.imap.ok ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'}>
                        IMAP: {testResult.imap.ok ? 'OK' : `Failed — ${testResult.imap.error}`}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Set primary toggle */}
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-slate-300">
                <input type="checkbox" checked={form.set_primary} onChange={e => setForm({ ...form, set_primary: e.target.checked })} />
                Make this my primary sender
              </label>

              {/* Say why Test is disabled. The server fields are hidden now, so a
                  greyed-out button was otherwise unexplained. */}
              {(() => {
                const missing = !form.email ? 'your email address'
                  : !form.smtp.password ? 'your password'
                  : (!form.smtp.host || !form.imap.host) ? 'the server settings above'
                  : null;
                return missing && (
                  <p className="text-xs text-gray-500 dark:text-slate-500">
                    Enter {missing} to test the connection.
                  </p>
                );
              })()}

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleTest}
                  disabled={testing || !form.email || !form.smtp.password || !form.smtp.host || !form.imap.host}
                  className="flex-1 px-4 py-2.5 border border-blue-600 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:border-gray-300 disabled:text-gray-400 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                >
                  {testing ? <><div className="animate-spin w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" /> Testing…</> : <>Test connection</>}
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !testResult?.ok}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium rounded-lg transition-all flex items-center justify-center gap-2"
                  title={!testResult?.ok ? 'Run a successful test first' : 'Save'}
                >
                  {saving ? <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> Saving…</> : <>Save account</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Help card */}
      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100 mb-2">How it works</h3>
              <ul className="text-gray-600 dark:text-slate-400 leading-relaxed space-y-1 text-sm">
                <li>1. <strong>Enter your email address.</strong> We'll automatically fill in the right server settings for both personal emails and work emails — you don't need to look up host or port numbers.</li>
                <li>2. <strong>Enter your email password.</strong> For personal emails, most providers require an <strong>app password</strong> (generated inside your email account's security settings) instead of your regular login password. For work emails, use the password your IT team gave you.</li>
                <li>3. <strong>Click Test Connection.</strong> We'll check two things in the background: that we can send email from your address (SMTP) and receive/read email for your address (IMAP). Both must pass before you can save.</li>
                <li>4. <strong>Click Save.</strong> Your account is now verified and linked. From here on, every email you send goes out from your real address, and all your sent messages plus incoming replies appear inside the <strong>Inbox</strong> section in the sidebar.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailSettings;
